"use client";
import React, { useRef, useState } from "react";
import { CombinedGraphQLErrors } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { faPlusCircle, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import dayjs from "dayjs";
import { useRouter } from "next/navigation";

import Button from "@/components/button";
import { Textarea, TextInput } from "@/components/forms";
import IfUserIs from "@/components/if-user-is";
import { ButtonBar, Module } from "@/components/layouts";
import { ButtonGroup } from "@/components/layouts/button-group";
import UserError from "@/components/user-error";
import { ThreadType } from "@/gql/generated/graphql";
import { MUTATE_CREATE_POST } from "@/gql/queries/mutate-create-post";
import { useModal } from "@/hooks/use-modal";

import ModalTitle from "./modal-title";
import PostBanned from "./post-banned";
import PostRateLimited from "./post-rate-limited";
import UserBanned from "./user-banned";
import UserUnverified from "./user-unverified";

const CHARACTERS_REGEX = /[a-zA-Z0-9]/;

const CreateThread = () => {
  const { openModal, closeModal } = useModal();
  const router = useRouter();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const selectTypeRef = useRef<HTMLSelectElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const [createPost] = useMutation(MUTATE_CREATE_POST, {
    onCompleted: (data) => {
      router.push(`/community/general/thread/${data.createPost.id}`);
    },
    onError: (e) => {
      if (CombinedGraphQLErrors.is(e)) {
        const error = e.errors[0].extensions;

        if (error?.code) {
          switch (error.code) {
            case "POST_RATE_LIMITED":
              openModal(() => <PostRateLimited />);
              break;

            case "POST_BANNED":
              openModal(() => (
                <PostBanned
                  bannedUntil={dayjs().to(
                    // @ts-ignore
                    dayjs(error?.extensions?.expiry as string),
                  )}
                />
              ));
              break;

            case "USER_BANNED":
              openModal(() => <UserBanned />);
              break;

            case "USER_UNVERIFIED":
              openModal(() => <UserUnverified />);
              break;
          }
        }

      }
    }
  });

  const [error, setError] = useState<Boolean>(false);

  const onPostClicked = async () => {
    if (!titleInputRef?.current?.value || !textInputRef?.current?.value) return;

    if (
      !CHARACTERS_REGEX.test(titleInputRef.current.value) ||
      !textInputRef.current.value.trim()
    ) {
      setError(true);
      return;
    }

    const postType = selectTypeRef.current?.value ?? "NORMAL";

    await createPost({
      variables: {
        title: titleInputRef.current.value,
        text: textInputRef.current.value,
        type: postType as ThreadType,
        endDate: postType === "COMPETITION" ? new Date(endDateRef.current?.value ?? "").valueOf() : null,
      },
    });

    closeModal();
  };

  return (
    <div>
      <ModalTitle>Create a new thread</ModalTitle>
      {error && <UserError errors={["Please enter a title and post text"]} />}
      <Module>
        <TextInput
          type="text"
          name="thread-title"
          label="Thread title"
          id="thread-title-input"
          ref={titleInputRef}
        />
      </Module>
      <Module>
        <Textarea
          ref={textInputRef}
          label="Your post"
          id="thread-post-textarea"
          maxLength={2000}
        />
      </Module>
      <IfUserIs admin>
        <Module>
          Thread type:{" "}
          <select ref={selectTypeRef}>
            <option value="NORMAL">Normal</option>
            <option value="COMPETITION">Competition</option>
          </select>
        </Module>
        <Module>
          <TextInput
            type="text"
            name="thread-end-date"
            label="End date"
            id="thread-end-date-input"
            defaultValue={dayjs().add(5, "days").format("YYYY-MM-DD HH:mm:ss")}
            ref={endDateRef}
          />
        </Module>
      </IfUserIs>
      <ButtonBar>
        <ButtonGroup align="right">
          <Button
            icon={faTimesCircle}
            onClick={closeModal}
            type="negative"
          >
            Cancel
          </Button>
          <Button
            icon={faPlusCircle}
            onClick={onPostClicked}
            type="positive"
          >
            Create Thread
          </Button>
        </ButtonGroup>
      </ButtonBar>
    </div>
  );
};

export default CreateThread;
